import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { get, del } from '../services/api/apiClient';
import { useApi, useApiMutation } from '../services/api/useApi';
import { CardSpinner, useToast } from '../components/ui';
import AddAccountModal from '../components/account/AddAccountModal';

function SocialAccountPage({ embedded = false }) {
  const { toast, confirm } = useToast();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // 獲取帳號列表
  const { data: accountsData, loading, error: apiError, execute: reloadAccounts } = useApi(
    () => get('/user/social-accounts/account-settings'),
    {
      immediate: true,
      onError: (err) => {
        console.error('❌ Failed to load accounts:', err);
        const errorMessage = err?.message || err?.error?.message || '載入帳號失敗';

        if (err?.status === 401 || err?.response?.status === 401) {
          toast.error('請先登入');
        } else {
          toast.error(errorMessage);
        }
      }
    }
  );

  const accounts = accountsData?.accounts || [];
  const accountsLimit = 100;
  const currentAccounts = accounts.length;

  useEffect(() => {
    if (apiError) {
      console.error('API Error:', apiError);
    }
  }, [apiError]);

  // 刪除帳號
  const { execute: executeDelete } = useApiMutation((accountId) => del(`/accounts/${accountId}`), {
    onSuccess: () => {
      toast.success('帳號刪除成功');
      reloadAccounts();
    },
    onError: (error) => {
      console.error('💥 Delete failed:', error);
      const errorMessage = error?.message || error?.error?.message || '刪除失敗';
      toast.error(errorMessage);
    }
  });

  const handleDeleteAccount = async (accountId, accountName) => {
    const confirmed = await confirm({
      title: '刪除帳號',
      message: `確定要刪除帳號 "${accountName}" 嗎？`,
      confirmText: '確認',
      cancelText: '取消',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      await executeDelete(accountId);
    } catch (error) {
      console.error('Delete account error caught:', error);
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      threads: 'simple-icons:threads',
      instagram: 'mdi:instagram',
      facebook: 'mdi:facebook'
    };
    return icons[platform] || 'mdi:account';
  };

  const getPlatformBgClass = (platform) => {
    const classes = {
      threads: 'bg-gray-900',
      instagram: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
      facebook: 'bg-blue-600'
    };
    return classes[platform] || 'bg-gray-500';
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50 p-6'}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">社群帳號管理</h1>
          <p className="text-gray-500">管理您已連接的社群媒體帳號</p>
        </div>

        {/* Stats Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 mb-1">已連接帳號</div>
              <div className="text-3xl font-bold text-gray-900">
                {currentAccounts}
                <span className="text-lg font-normal text-gray-400">/{accountsLimit}</span>
              </div>
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              disabled={currentAccounts >= accountsLimit}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon icon="mdi:plus" className="text-lg" />
              新增帳號
            </button>
          </div>
        </div>

        {/* Account List */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {loading ? (
            <CardSpinner text="載入帳號中..." minHeight="300px" />
          ) : accounts.length === 0 ? (
            <div className="py-16 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon icon="mdi:account-off-outline" className="text-4xl text-gray-400" />
              </div>
              <p className="text-gray-900 font-medium mb-2">尚未連接任何帳號</p>
              <p className="text-gray-500 text-sm mb-6">點擊「新增帳號」開始連接您的社群帳號</p>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                <Icon icon="mdi:plus" className="text-lg" />
                新增帳號
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {accounts.map((account) => (
                <div key={account.id} className="flex items-center justify-between p-5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    {/* Platform Icon */}
                    <div className={`w-12 h-12 rounded-full ${getPlatformBgClass(account.platform)} flex items-center justify-center`}>
                      <Icon
                        icon={getPlatformIcon(account.platform)}
                        className="text-xl text-white"
                      />
                    </div>

                    {/* Account Info */}
                    <div>
                      <div className="font-semibold text-gray-900">
                        @{account.account_name || 'unknown'}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-2">
                        <span className="capitalize">{account.platform}</span>
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                        <span className="text-green-600">已連接</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <button
                    onClick={() => handleDeleteAccount(account.id, account.account_name)}
                    className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    移除
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Account Modal */}
      <AddAccountModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          setIsAddModalOpen(false);
          reloadAccounts();
        }}
      />
    </div>
  );
}

export default SocialAccountPage;
